import * as Fmt from "../format";
import {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "../decision-thresholds";

const SMALL_NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
] as const;

function yearCount(years: number): string {
  const rounded = Math.max(0, Math.round(years));
  return SMALL_NUMBER_WORDS[rounded] ?? rounded.toLocaleString("en-AU");
}

/** Board-facing name for the term-loan refinance/hold/exit cashflow strategy.
 * The internal model field keeps its legacy name for compatibility. */
export function gearedRefinanceExitLabel(holdYears: number): string {
  const years = Math.max(0, Math.round(holdYears));
  return `Geared IRR — refinance, hold ${yearCount(years)} year${years === 1 ? "" : "s"}, then exit`;
}

export function gearedRefinanceExitContext(holdYears: number): string {
  const years = Math.max(0, Math.round(holdYears));
  return `Matched ${yearCount(years)}-year refinance-and-exit strategy`;
}

export function gearedRefinanceExitFootnote(
  holdYears: number,
  termLoanLvrFormatted: string,
): string {
  const years = Math.max(0, Math.round(holdYears));
  return `${years}y hold on ${termLoanLvrFormatted} term loan`;
}

export function developerEquityRequiredLvrFootnote(
  equityShareFormatted: string,
  constructionLvrFormatted: string,
): string {
  return `${equityShareFormatted} of total cost (${constructionLvrFormatted} LVR)`;
}

/** Shared across SPA, PDF, and board-facing exports for money-multiple KPI footnotes. */
export const MONEY_MULTIPLE_EQUITY_FOOTNOTE =
  "Equity proceeds at PC (net realisation − facility − debt) ÷ equity contributed";

export function moneyMultipleExitEyebrow(exitYear: number): string {
  // A5: developerExitYear is PROJECT years from T0 (decision recorded 2026-09-02) — labels say so.
  return `Money multiple (project Y${exitYear} exit)`;
}

export function moneyMultipleEarlyExitFootnote(exitYear: number): string {
  return `Equity distributions through the project Y${exitYear} exit (PC refinance, net rent after term-loan service, exit sale net of the term loan) ÷ equity contributed incl. any PC top-up · not time-adjusted — read with the geared equity IRR`;
}

export const BREAK_EVEN_RENT_UNAVAILABLE_FOOTNOTE =
  "No feasible building-rent break-even found in the tested range";

export const BREAK_EVEN_ENROLMENT_UNAVAILABLE_FOOTNOTE =
  "No feasible enrolment break-even found in the tested range";

export const BREAK_EVEN_ENROLMENT_CONVERGED_FOOTNOTE =
  "Steady-state students where EBITDA = 0";

/** Total occupancy cost disclosure when the building-rent break-even solver converges. */
export function breakEvenTotalOccupancyFootnote(
  totalOccupancyCost: string,
): string {
  return `Total occupancy cost ${totalOccupancyCost}/yr incl. amenity rent`;
}

/** Annualised building-rent break-even display (value line). */
export function breakEvenBuildingRentValue(formattedAmount: string): string {
  return `${formattedAmount}/yr`;
}

/** Building-rent break-even KPI label — shared across SPA, Excel, PDF, and compare. */
export const BREAK_EVEN_RENT_Y1_LABEL = "Building gross rent at Y1 break-even";

export const RENT_PCT_REVENUE_Y1_LABEL = "Rent / revenue (Y1)";
export const RENT_PCT_REVENUE_SENSITIVITY_HEADER = "Rent / revenue";
export const RENT_PCT_REVENUE_STEADY_LABEL = "Rent / revenue (steady state)";
export const RENT_PCT_REVENUE_Y1_AVG_LABEL = "Rent / revenue (Y1 / avg)";
/** Term-average occupancy-cost ratio on its own (Excel Headline KPIs row) — a single value, so not the two-value Y1 / avg hero label. */
export const RENT_PCT_REVENUE_AVG_LABEL = "Rent / revenue (avg)";

/** Compact hero KPI labels (report SPA + PDF cover). */
export const Y1_OCCUPANCY_COST_LABEL = "Y1 occupancy cost";
export const Y1_EBITDA_LABEL = "Y1 EBITDA";
export const AVG_EBITDA_LABEL = "Avg EBITDA";

/** Cost-ledger subtotal row — gross pre-finance TDC (distinct from PRE_FINANCE_TDC_LABEL). */
export const TOTAL_COST_LEDGER_LABEL = "Total cost";

/** Operator NPV KPI footnote — shared across SPA, PDF, and report. */
export function operatorNpvDiscountFootnote(
  discountRateFormatted: string,
): string {
  return `At ${discountRateFormatted} discount rate`;
}

/** Operator IRR KPI footnote when the solver converges. */
export const OPERATOR_IRR_CONVERGED_FOOTNOTE =
  "Across operator cashflow stream";

/** Model-horizon year reference for KPI footnotes (e.g. steady-state revenue). */
export function modelHorizonYearFootnote(modelYear: number): string {
  return `Y${modelYear}`;
}

/** Operator payback KPI footnote — optional steady-state revenue for hero/decision views. */
export function operatorPaybackFootnote(
  operatorCapexFormatted: string,
  steadyStateRevenueFormatted?: string,
): string {
  const base = `From operator capex ${operatorCapexFormatted}`;
  if (steadyStateRevenueFormatted) {
    return `${base} · steady-state revenue ${steadyStateRevenueFormatted}`;
  }
  return base;
}

/** Abbreviated PDF cover summary: discount rate + payback on one line. */
export function operatorCoverNpvFootnote(
  discountRateFormatted: string,
  paybackYear: number | null | undefined,
): string {
  return `@ ${discountRateFormatted} · payback ${Fmt.payback(paybackYear)}`;
}

/** Payback clause for narrative prose (exec summary, recommendation). */
export function operatorPaybackProseClause(
  paybackYear: number | null | undefined,
): string {
  return paybackYear
    ? `in ${Fmt.payback(paybackYear)}`
    : "beyond the model horizon";
}

/** Caption under the operator annual cashflow table. */
export function operatorPaybackCaption(
  paybackYear: number | null | undefined,
): string {
  return paybackYear
    ? `Operator payback occurs in ${Fmt.payback(paybackYear)} (cumulative cashflow turns positive).`
    : "Operator cumulative cashflow remains negative across the full horizon.";
}

/** Y1 occupancy-cost KPI footnote on report (abbreviated). */
export function y1OccupancyCostReportFootnote(
  termAvgFormatted: string,
): string {
  return `Term average ${termAvgFormatted}`;
}

/** Y1 occupancy-cost KPI footnote on PDF cover (includes methodology disclosure). */
export function y1OccupancyCostCoverFootnote(termAvgFormatted: string): string {
  return `Term occupancy cost ${termAvgFormatted} · simple mean of annual ratios`;
}

/** Opening-ramp liquidity narrative lead (report SPA, operator economics, PDF). */
export function rampOpeningLiquidityProseLead(): string {
  return "The opening ramp requires";
}

export const RAMP_OPENING_LIQUIDITY_SUPPORT_SUFFIX = "of liquidity support";

/** Operator recommendation / report "why this matters" paragraph (PDF). */
export function operatorWhyThisMattersRecommendationProse(params: {
  y1OccupancyShare: string;
  y1Ebitda: string;
  rampReserves: string;
  operatorNpv: string;
  discountRate: string;
  paybackYear: number | null | undefined;
}): string {
  return `${Y1_OCCUPANCY_COST_LABEL} is ${params.y1OccupancyShare} of revenue; ${Y1_EBITDA_LABEL} is ${params.y1Ebitda}. ${rampOpeningLiquidityProseLead()} ${params.rampReserves} ${RAMP_OPENING_LIQUIDITY_SUPPORT_SUFFIX}. Operator NPV is ${params.operatorNpv} at ${params.discountRate} discount rate, with payback ${operatorPaybackProseClause(params.paybackYear)}.`;
}

/** Total tax (term) KPI footnote — shared across SPA, PDF, and report. */
export function operatorTotalTaxFootnote(
  taxRateFormatted: string,
  modelYears: number,
): string {
  return `${taxRateFormatted} rate, ${modelYears}-yr term`;
}

/** Operator NPV value line for status/summary blocks (recommendation, dashboards). */
export function operatorNpvStatusDetail(
  npvFormatted: string,
  discountRateFormatted: string,
): string {
  return `${npvFormatted} at ${discountRateFormatted} discount rate`;
}

/** Monte Carlo / risk breach label for structural DSCR covenant stress. */
export const DSCR_STRUCTURAL_BREACH_LABEL =
  "Developer term-loan DSCR < 1.25× reference (structural; cap-rate / rate driven, not rent)";

export const DSCR_REFERENCE_TARGET = "1.25×";

export const DSCR_STATUS_BAD_LABEL = `Below ${DSCR_REFERENCE_TARGET} reference`;

export const DSCR_HERO_FOOTNOTE = `Developer NOI ÷ debt service · reference target ≥ ${DSCR_REFERENCE_TARGET}`;

export const DSCR_NOT_APPLICABLE_LABEL = "Not applicable";

export function dscrDebtFacilityFootnote(
  noiFormatted: string,
  annualServiceFormatted: string,
): string {
  return `NOI ${noiFormatted} / annual service ${annualServiceFormatted}; structural and invariant to rent`;
}

export const ALL_IN_COST_FOOTNOTE = "Pre-finance TDC + interest + line fee";
export const ALL_IN_COST_LABEL = "All-in cost including interest and fees";
export const ALL_IN_COST_SHORT_LABEL = "All-in cost";

export const TOTAL_FUNDING_SOURCES_LABEL = "Total funding sources";
export const SOURCES_LESS_USES_DIFFERENCE_LABEL =
  "Sources less uses difference";

export const DEVELOPER_EQUITY_REQUIRED_FOOTNOTE =
  "Pre-finance TDC less gross debt capacity; unchanged by contribution";

export const DEVELOPER_EQUITY_INJECTION_TABLE_NOTE =
  "Pre-finance TDC less gross capacity";

export function constructionDebtCostFootnote(
  interestFormatted: string,
  lineFeeFormatted: string,
): string {
  return `${interestFormatted} interest + ${lineFeeFormatted} line fee`;
}

export const OPERATOR_CONTRIBUTION_FUNDING_FOOTNOTE =
  "Funding source that replaces debt; does not reduce TDC or developer equity";

export const OPERATOR_CONTRIBUTION_BOARD_NOTE =
  "Funding source — replaces actual developer debt; it does not reduce gross TDC or developer equity.";

export const INSPIRED_REDDAM_CONTRIBUTION_LABEL =
  "Inspired/Reddam contribution";
export const BEFORE_INSPIRED_REDDAM_CONTRIBUTION_FOOTNOTE =
  "Before Inspired/Reddam contribution";

export const CONSTRUCTION_FACILITY_AFTER_CONTRIBUTION_TABLE_NOTE =
  "Gross capacity less operator contribution";

export function operatorContributionAppliedFootnote(
  contributionFormatted: string,
): string {
  return `${contributionFormatted} contribution replaces debt`;
}

export const CONSTRUCTION_DEBT_CAP_INPUT_FOOTNOTE =
  "Maximum leverage applied to lender-eligible costs; not the actual debt share";

export const INPUT_DEBT_CAP_LABEL = "Input debt cap / lender-eligible base";

export function grossDebtCapacityFootnote(
  grossDebtShareFormatted: string,
): string {
  return `${grossDebtShareFormatted} of pre-finance TDC before contribution`;
}

export function actualConstructionDebtFootnote(
  actualDebtShareFormatted: string,
): string {
  return `${actualDebtShareFormatted} of pre-finance TDC after contribution`;
}

export function termLoanLimitFootnote(lvrFormatted: string): string {
  return `${lvrFormatted} of gross realisation`;
}

export const CONSTRUCTION_DEBT_COST_TABLE_NOTE = "Interest + line fee";
export const CONSTRUCTION_INTEREST_LABEL = "Construction interest";
export const LINE_FEE_ONE_OFF_LABEL = "Line fee (one-off)";

export const TERM_LOAN_DSCR_TABLE_NOTE = "NOI ÷ service";

export function operatorRentAffordabilityHeroFootnote(
  y1Status: "bad" | "warn" | "good",
): string {
  const statusPhrase =
    y1Status === "bad"
      ? "breaks ceiling"
      : y1Status === "warn"
        ? "in caution zone"
        : "within target";
  return `Caution >${Fmt.pct(OPERATOR_AFFORDABILITY_CAUTION, 0)} · ceiling ${Fmt.pct(OPERATOR_AFFORDABILITY_CEILING, 0)}. Y1 ${statusPhrase}.`;
}

export function contributionTimingFootnote(
  timing: string = "progressive",
): string {
  return `Contribution timing: ${timing}`;
}

export const OPERATOR_EBITDA_NEGATIVE_Y1_BREACH_LABEL =
  "Operator EBITDA negative — Year 1";

export function operatorEbitdaNegativePostRampBreachLabel(
  rampYears: number,
): string {
  return `Operator EBITDA negative — Year ${rampYears + 1} (post-ramp)`;
}

/** Ramp funding need KPI footnote (cover / report summary cards). */
export const RAMP_FUNDING_NEED_FOOTNOTE =
  "Peak modelled operator liquidity support";

export const RAMP_FUNDING_NEED_LABEL = "Ramp funding need";

/** Ramp reserves required KPI footnote (headline economics detail). */
export const RAMP_RESERVES_REQUIRED_FOOTNOTE =
  "Peak operator cash trough (incl. fitout capex); reserves needed to survive ramp";

export const RAMP_RESERVES_REQUIRED_LABEL = "Ramp reserves required";

export const EBITDA_NEGATIVE_YEARS_DETAIL_FOOTNOTE =
  "Number of modelled years with negative operator EBITDA";

export function ebitdaNegativeYearsCountFootnote(count: number): string {
  return `${count} EBITDA-negative year${count === 1 ? "" : "s"}`;
}

export const PRE_FINANCE_TDC_FOOTNOTE =
  "Land + build + soft costs + fees · Excludes construction interest and line fee";

export function developerProfitFootnote(marginOnCostFormatted: string): string {
  return `Margin on cost ${marginOnCostFormatted}`;
}

export const DEVELOPER_YIELD_ON_COST_FOOTNOTE = "NOI / total cost";

/** Key-ratios table context notes (PDF section 06). */
export const MARGIN_ON_COST_RATIO_NOTE = "Profit / total developer cost";
export const MARGIN_ON_COST_LABEL = "Margin on cost";
export const DEVELOPER_PROFIT_LABEL = "Developer profit";
export const OPERATOR_NPV_LABEL = "Operator NPV";
export const OPERATOR_IRR_LABEL = "Operator IRR";
export const OPERATOR_PAYBACK_LABEL = "Payback";
export const PRE_FINANCE_TDC_LABEL = "Pre-finance TDC";
export const DEVELOPER_YIELD_ON_COST_RATIO_NOTE = "NOI / total developer cost";
export const GROSS_DEBT_CAPACITY_RATIO_NOTE =
  "Before operator contribution; distinct from actual debt";
export const ACTUAL_CONSTRUCTION_DEBT_RATIO_NOTE =
  "After operator contribution; gross TDC is unchanged";
export const LTV_RATIO_NOTE = "Term loan / gross realisation";
export const Y1_RENT_PER_STUDENT_RATIO_NOTE =
  "Year-1 gross rent per enrolled student";
export const Y1_REVENUE_PER_STUDENT_RATIO_NOTE =
  "Year-1 total revenue per student";
export const Y1_EBITDA_MARGIN_RATIO_NOTE = "Year-1 EBITDA / total revenue";
export const Y1_RENT_PCT_REVENUE_RATIO_NOTE =
  "Year-1 rent as % of total revenue";

/** Term-loan sufficiency pill + footnote copy (SPA debt facility + PDF section 08). */
export const TERM_LOAN_SUFFICIENCY_LABEL = "Term loan sufficiency";
export const TERM_LOAN_SUFFICIENCY_PASS_LABEL = "Pass";
export const TERM_LOAN_SUFFICIENCY_WARN_LABEL = "Warn";
export const TERM_LOAN_SUFFICIENT_LABEL = "Term loan sufficient";
export const TERM_LOAN_EQUITY_TOPUP_LABEL = "Equity top-up required";
export const TERM_LOAN_SUFFICIENT_FOOTNOTE =
  "Covers facility + debt costs at PC";

export function termLoanShortfallFootnote(shortfallFormatted: string): string {
  return `Shortfall ${shortfallFormatted}`;
}

/** Base rent-gap direction labels (model gapRentY1 = calibrated − scenario). */
export const RENT_GAP_ABOVE_REFERENCE_LABEL =
  "Ask above developer financing reference";
export const RENT_GAP_BELOW_REFERENCE_LABEL =
  "Ask below developer financing reference";
export const RENT_GAP_NEAR_REFERENCE_COVER_LABEL =
  "Ask near developer financing reference";
export const RENT_GAP_EQUALS_REFERENCE_LABEL =
  "Ask equals developer financing reference";

/** PDF cover pill — coarse 5% band around the financing-equivalent reference. */
export function rentGapFinancingReferenceCoverLabel(
  gapRentY1: number,
  scenarioRentY1: number,
  threshold = 0.05,
): string | null {
  if (
    !Number.isFinite(gapRentY1) ||
    !Number.isFinite(scenarioRentY1) ||
    scenarioRentY1 <= 0
  ) {
    return null;
  }
  const gapPct = -gapRentY1 / scenarioRentY1;
  if (gapPct > threshold) return RENT_GAP_ABOVE_REFERENCE_LABEL;
  if (gapPct < -threshold) return RENT_GAP_BELOW_REFERENCE_LABEL;
  return RENT_GAP_NEAR_REFERENCE_COVER_LABEL;
}

/** Rent-bridge KPI hint — exact sign with affordability disclaimers. */
export function rentGapFinancingReferenceHint(gapRentY1: number): string {
  if (gapRentY1 > 0) {
    return `${RENT_GAP_BELOW_REFERENCE_LABEL} — not an affordability conclusion`;
  }
  if (gapRentY1 < 0) {
    return `${RENT_GAP_ABOVE_REFERENCE_LABEL} — review alongside operator ceilings`;
  }
  return RENT_GAP_EQUALS_REFERENCE_LABEL;
}

export function developerYieldOnCostDetailFootnote(
  noiFormatted: string,
): string {
  return `NOI ${noiFormatted} / total cost`;
}

export function developerNoiOnEquityFootnote(equityFormatted: string): string {
  return `Stabilised Y1 NOI / equity ${equityFormatted}`;
}

export function developerNoiOnEquityReportFootnote(
  equityFormatted: string,
): string {
  return `${NOI_ON_DEVELOPER_EQUITY_DEFINITION} (${equityFormatted})`;
}

export const NOI_ON_DEVELOPER_EQUITY_LABEL =
  "NOI / developer equity (before interest)";
export const NOI_ON_DEVELOPER_EQUITY_DEFINITION =
  "Stabilised Year-1 NOI / developer equity (before interest)";

export const DEVELOPER_NOI_ON_EQUITY_RATIO_NOTE =
  "Stabilised Y1 NOI / developer equity before interest";

/** Net realisation KPI footnote — exit-year aware (matches PDF headline economics). */
export function netRealisationFootnote(
  exitYear: number,
  modelYears: number,
  capRateFormatted: string,
  noiFormatted: string,
): string {
  if (exitYear < modelYears) {
    return `Exit-year (project Y${exitYear}) valuation @ ${capRateFormatted}`;
  }
  return `NOI ${noiFormatted} @ ${capRateFormatted}`;
}

export const PEAK_CASH_TROUGH_FOOTNOTE =
  "Minimum cumulative cashflow including fitout outlay";

export const DEVELOPER_BOARD_YIELD_ON_GROSS_TDC_FOOTNOTE =
  "Stabilised Y1 NOI / gross TDC";

export const GROSS_TOTAL_DEVELOPMENT_COST_LABEL =
  "Gross total development cost";
export const GROSS_TDC_LABEL = "Gross TDC";
export const GROSS_TDC_USES_LABEL = "Gross TDC (uses)";
export const YIELD_ON_COST_LABEL = "Yield on cost";
export const EXIT_CAP_RATE_LABEL = "Exit cap rate";
export const DEVELOPMENT_SPREAD_LABEL = "Development spread";

/** Compact sensitivity table headers (report SPA + PDF). */
export const SENSITIVITY_DEBT_STACK_HEADER = "Gross / actual / peak debt";
export const SENSITIVITY_CONSTRUCTION_FINANCE_HEADER = "Interest / fee";
export const UNGEARED_RETURN_SENSITIVITY_HEADER = "Ungeared return";
export const GEARED_RETURN_SENSITIVITY_HEADER = "Geared return";
export const UNGEARED_GEARED_RETURN_SENSITIVITY_HEADER =
  "Ungeared / geared return";

export function matchedHorizonLabel(
  exitYear: number,
  modelYears: number,
): string {
  return exitYear < modelYears
    ? `project Y${exitYear} hold-and-sell`
    : "sale at completion";
}

/** A1: exit-basis disclosure for the rent bridge — the financing-equivalent reference and the
 *  developer-margin component both move with developerExitYear, so every bridge surface names
 *  the exit basis its figures were built on (same hub-label pattern as matchedHorizonLabel, #70). */
export function rentBridgeExitBasisLabel(
  exitYear: number,
  modelYears: number,
): string {
  return `Exit basis: ${matchedHorizonLabel(exitYear, modelYears)}`;
}

export function developerPrimaryUngearedFootnote(
  hurdleFormatted: string,
  horizonLabel: string,
): string {
  return `Primary · Stated ungeared project hurdle ${hurdleFormatted} · ${horizonLabel}`;
}

/** Compact ungeared hurdle footnote for executive-summary hero KPIs (Y{n} / PC). */
export function developerPrimaryUngearedHeroFootnote(
  hurdleFormatted: string,
  compactHorizonLabel: string,
): string {
  return `Stated ungeared project hurdle ${hurdleFormatted} · ${compactHorizonLabel}`;
}

type StatedHurdleStatus = "met" | "marginal" | "below";

/** Cover / compact stated-hurdle status phrase (PDF cover counterparty diagnostics). */
export function statedHurdleStatusLabel(
  status: StatedHurdleStatus,
  hurdleMet: boolean,
): string {
  switch (status) {
    case "marginal":
      return hurdleMet ? "Marginally above hurdle" : "Marginally below hurdle";
    case "met":
      return "Hurdle met";
    case "below":
      return "Below hurdle";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

/** Abbreviated stated-hurdle footnote for the PDF cover (space-constrained). */
export function statedHurdleCoverFootnote(
  hurdleFormatted: string,
  status: StatedHurdleStatus,
  hurdleMet: boolean,
): string {
  return `Stated hurdle ${hurdleFormatted} · ${statedHurdleStatusLabel(status, hurdleMet)}`;
}

/** Board report / SPA developer-decision pill labels (matched-horizon ungeared IRR). */
export const STATED_HURDLE_UNAVAILABLE_PILL_LABEL =
  "Hurdle evidence unavailable";
export const STATED_HURDLE_MET_PILL_LABEL = "Stated hurdle met";
export const STATED_HURDLE_MARGINAL_PILL_LABEL = "At stated hurdle";
export const STATED_HURDLE_BELOW_PILL_LABEL = "Below stated hurdle";

export function developerDecisionPillLabel(
  status: StatedHurdleStatus,
  available: boolean,
): string {
  if (!available) return STATED_HURDLE_UNAVAILABLE_PILL_LABEL;
  switch (status) {
    case "met":
      return STATED_HURDLE_MET_PILL_LABEL;
    case "marginal":
      return STATED_HURDLE_MARGINAL_PILL_LABEL;
    case "below":
      return STATED_HURDLE_BELOW_PILL_LABEL;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export const GROSS_DEBT_CAPACITY_LABEL = "Gross debt capacity";
export const ACTUAL_CONSTRUCTION_DEBT_LABEL = "Actual construction debt";
export const OPERATOR_CONTRIBUTION_LABEL = "Operator contribution";
export const DEVELOPER_EQUITY_LABEL = "Developer equity";
export const CONSTRUCTION_DEBT_COST_LABEL = "Construction debt cost";
export const PEAK_CONSTRUCTION_DEBT_LABEL = "Peak construction debt";
export const TERM_LOAN_LIMIT_LABEL = "Term loan limit";
export const DEVELOPER_TERM_LOAN_DSCR_LABEL = "Developer term-loan DSCR (Y1)";
export const TERM_LOAN_DSCR_TABLE_LABEL = "Term-loan DSCR (Y1)";
export const TOTAL_DEBT_COST_LABEL = "Total debt cost";
export const EQUITY_REQUIRED_LABEL = "Equity required";

/** Compact gauge-ring hurdle status (developer IRR gauges). */
export const GAUGE_HURDLE_MET_LABEL = "\u2713 Hurdle met";
export const GAUGE_AT_HURDLE_LABEL = "At hurdle";
export const GAUGE_BELOW_HURDLE_LABEL = "Below hurdle";

export function gaugeHurdleStatusLabel(
  met: boolean,
  target: number | null | undefined,
  value: number,
  marginalBand = 0.005,
): string {
  if (met) return GAUGE_HURDLE_MET_LABEL;
  if (
    target !== null &&
    target !== undefined &&
    value >= target - marginalBand
  ) {
    return GAUGE_AT_HURDLE_LABEL;
  }
  return GAUGE_BELOW_HURDLE_LABEL;
}

/** Uppercase developer-hurdle pill labels (SPA executive / developer economics). */
export const HURDLE_STATUS_MET_PILL_LABEL = "HURDLE MET";
export const HURDLE_STATUS_MARGINAL_PILL_LABEL = "AT HURDLE";
export const HURDLE_STATUS_BELOW_PILL_LABEL = "BELOW HURDLE";

export function hurdleStatusPillLabel(status: StatedHurdleStatus): string {
  switch (status) {
    case "met":
      return HURDLE_STATUS_MET_PILL_LABEL;
    case "marginal":
      return HURDLE_STATUS_MARGINAL_PILL_LABEL;
    case "below":
      return HURDLE_STATUS_BELOW_PILL_LABEL;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export const GEARED_REFERENCE_LABEL = "Cashflow-equivalent geared reference";

export function developerGearedReferenceHeroFootnote(
  referenceFormatted: string | null,
): string {
  if (!referenceFormatted) {
    return `${GEARED_REFERENCE_LABEL} unavailable`;
  }
  return `${GEARED_REFERENCE_LABEL} ${referenceFormatted}`;
}

/** Abbreviated geared reference footnote for the PDF cover (space-constrained). */
export const GEARED_REFERENCE_COVER_UNAVAILABLE =
  "Geared reference unavailable";

export function gearedReferenceCoverFootnote(
  referenceFormatted: string | null,
): string {
  if (!referenceFormatted) {
    return GEARED_REFERENCE_COVER_UNAVAILABLE;
  }
  return `Geared reference ${referenceFormatted}`;
}

export function developerGearedReferenceFootnote(
  referenceFormatted: string | null,
  equityFormatted?: string,
): string {
  if (!referenceFormatted) {
    return `Secondary · ${GEARED_REFERENCE_LABEL} unavailable`;
  }
  const base = `Secondary · ${GEARED_REFERENCE_LABEL} ${referenceFormatted}`;
  return equityFormatted ? `${base} · Equity ${equityFormatted}` : base;
}

export const STABILISED_YEAR1_NOI_FOOTNOTE =
  "Existing model stabilised Year-1 NOI basis";
export const STABILISED_YEAR1_NOI_LABEL = "Stabilised Year-1 NOI";

/** Developer Economics NOI Y1 KPI — composition footnote. */
export function stabilisedYear1NoiCompositionFootnote(
  includeCoveredPlayAreaGroundRent: boolean,
): string {
  return `Net rent + sports field + parking${
    includeCoveredPlayAreaGroundRent ? " + covered play area ground rent" : ""
  }`;
}

export const PROFIT_MARGIN_ON_COST_LABEL = "Profit / margin on cost";

export function matchedUngearedReturnLabel(horizon: string): string {
  return `Matched ungeared return — ${horizon}`;
}

export function matchedGearedReferenceLabel(horizon: string): string {
  return `Matched geared reference — ${horizon}`;
}

export function rentBurdenStatusDetail(
  y1ShareFormatted: string,
  termAvgFormatted: string,
): string {
  return `${y1ShareFormatted} Y1 revenue share · ${termAvgFormatted} term average (secondary)`;
}

/** Decision-metrics hero: Y1 EBITDA footnote with ramp reserve context. */
export function operatorOpeningSurvivabilityFootnote(
  negativeEbitdaYears: number,
  reservesFormatted: string,
): string {
  return `${ebitdaNegativeYearsCountFootnote(negativeEbitdaYears)} · ramp reserves ${reservesFormatted}`;
}

/** Decision-metrics hero: abbreviated operator NPV + IRR summary. */
export function operatorNpvHeroFootnote(
  discountRateFormatted: string,
  irrFormatted: string,
): string {
  return `Discounted at ${discountRateFormatted} · operator IRR ${irrFormatted}`;
}

export function openingLiquidityStatusDetail(
  reservesFormatted: string,
  negativeEbitdaYears: number,
): string {
  return `${reservesFormatted} ramp reserves · ${ebitdaNegativeYearsCountFootnote(negativeEbitdaYears)}`;
}

export function rentAffordabilityThresholdFootnote(
  phase: "opening" | "steady",
): string {
  const lead =
    phase === "opening"
      ? "Opening-year affordability"
      : "Final-year occupancy-cost ratio";
  return `${lead} · caution >${Fmt.pct(OPERATOR_AFFORDABILITY_CAUTION, 0)} · ceiling ${Fmt.pct(OPERATOR_AFFORDABILITY_CEILING, 0)}`;
}

/** Effective-rent block labels (SPA incentive panel, PDF rent bridge, Excel Rent Bridge sheet). */
export const EFFECTIVE_BUILDING_RENT_Y1_LABEL = "Effective building rent (Y1)";
export const EFFECTIVE_BUILDING_RENT_RATIO_LABEL =
  "Effective building rent / Y1 revenue (excl. amenity rent — not graded)";
export const EFFECTIVE_OCCUPANCY_COST_Y1_LABEL =
  "Effective occupancy cost (Y1)";
export const EFFECTIVE_OCCUPANCY_COST_RATIO_LABEL =
  "Effective occupancy cost / Y1 revenue (building + amenity rent − amortised incentive)";
/** Dollar row label shared by the Excel Rent Bridge sheet and the PDF rent-bridge table. */
export const EFFECTIVE_OCCUPANCY_COST_Y1_ROW_LABEL =
  "Effective occupancy cost Y1 (building + amenity rent − amortised incentive)";
export const EFFECTIVE_OCCUPANCY_COST_RATIO_EXCEL_LABEL =
  "Effective occupancy cost / revenue (Y1)";
/** Why the building-only effective ratio carries no affordability grade. */
export const EFFECTIVE_BUILDING_RENT_NOT_GRADED_NOTE =
  "Building rent only — excludes amenity landlord rent, so it is not an occupancy-cost ratio and is not graded against the affordability ceilings.";
/** Basis disclosure for the graded effective occupancy-cost ratio. */
export const EFFECTIVE_OCCUPANCY_COST_BASIS_NOTE =
  "Total effective lease cost — building rent plus amenity landlord rent, minus amortised incentive — on the same basis as the authoritative occupancy-cost ratio and graded against the same caution and hard ceilings.";

export const LEASE_INCENTIVE_METHODOLOGY =
  "effective rent = face rent − amortised incentive (ISCA / Savills methodology)";

export const LEASE_INCENTIVE_CALIBRATION_DISCLOSURE =
  "It is not fed into the financing-equivalent rent-reference calibration (the cost-to-rent bridge is computed on the developer cost stack, independent of the incentive).";

export const LEASE_INCENTIVE_RENT_FREE_CAVEAT =
  "Rent-free months are modelled as a lump-sum equivalent at commencement; actual cashflow timing may differ.";

/** SPA section lede for the face-vs-effective rent panel. */
export const LEASE_INCENTIVE_PANEL_LEDE = `Landlord contribution and rent-free period lower effective occupancy cost (${LEASE_INCENTIVE_METHODOLOGY}). The incentive is a developer cost at commencement, shown on the effective-rent side. ${LEASE_INCENTIVE_CALIBRATION_DISCLOSURE}`;

/** PDF / board-pack incentive disclosure block. */
export const LEASE_INCENTIVE_BOARD_DISCLOSURE = `Incentive amortisation reduces effective occupancy cost (${LEASE_INCENTIVE_METHODOLOGY}). The incentive is a developer cost at commencement, shown on the effective-rent side — it is NOT fed into the financing-equivalent rent-reference calibration (the cost-to-rent bridge is computed on the developer cost stack, independent of the incentive). ${LEASE_INCENTIVE_RENT_FREE_CAVEAT}`;
